import { useEffect, useMemo, useRef, useState } from "react";
import "./SalesTrend.css";

const HEIGHT = 260;
const PAD = { top: 18, right: 18, bottom: 30, left: 70 };
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const compactCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const count = new Intl.NumberFormat("en-US");
const METRICS = {
  sales: { label: "Sales", value: (point) => Number(point?.sales || 0), format: (value) => currency.format(value), axis: (value) => compactCurrency.format(value) },
  orders: { label: "Orders", value: (point) => Number(point?.orders || 0), format: (value) => count.format(value), axis: (value) => count.format(value) },
};
const UNIT_LABEL = { day: "day", week: "week", month: "month" };

const parse = (value) => new Date(`${value}T00:00:00Z`);
const shortDate = (value, options = { month: "short", day: "numeric" }) =>
  value ? new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(parse(value)) : "";
function periodLabel(point, granularity) {
  if (!point) return "";
  if (granularity === "month") return shortDate(point.start_date, { month: "long", year: "numeric" });
  if (granularity === "week" && point.start_date !== point.end_date) return `${shortDate(point.start_date)} – ${shortDate(point.end_date)}`;
  return shortDate(point.start_date, { weekday: "short", month: "short", day: "numeric" });
}
function axisLabel(point, granularity) {
  if (!point) return "";
  return granularity === "month" ? shortDate(point.start_date, { month: "short" }) : shortDate(point.start_date);
}

// A rounded axis maximum so gridlines land on readable values.
function niceStep(range, ticks = 4) {
  const raw = range / ticks || 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / magnitude;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * magnitude;
}

function useWidth(ref, fallback = 760) {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => { if (element.clientWidth) setWidth(element.clientWidth); };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

export default function SalesTrend({ trend, comparisonLabel = "Comparison" }) {
  const [metricKey, setMetricKey] = useState("sales");
  const [active, setActive] = useState(null);
  const frameRef = useRef(null);
  const width = useWidth(frameRef);
  const current = trend?.current || [];
  const previous = trend?.previous || null;
  const granularity = trend?.granularity || "day";
  const metric = METRICS[metricKey];

  const chart = useMemo(() => {
    const length = Math.max(current.length, previous?.length || 0);
    const values = [...current, ...(previous || [])].map(metric.value);
    const maxValue = Math.max(0, ...values);
    const minValue = Math.min(0, ...values);
    const step = niceStep(maxValue - minValue || 1);
    const top = Math.max(step, Math.ceil(maxValue / step) * step);
    const bottom = Math.floor(minValue / step) * step;
    const plotWidth = Math.max(10, width - PAD.left - PAD.right);
    const plotHeight = HEIGHT - PAD.top - PAD.bottom;
    const x = (index) => PAD.left + (length <= 1 ? plotWidth / 2 : (index * plotWidth) / (length - 1));
    const y = (value) => PAD.top + ((top - value) / (top - bottom || 1)) * plotHeight;
    const line = (points) => points.map((point, index) => `${index ? "L" : "M"}${x(index).toFixed(1)},${y(metric.value(point)).toFixed(1)}`).join(" ");
    const ticks = Array.from({ length: Math.round((top - bottom) / step) + 1 }, (_, index) => bottom + index * step);
    const labelEvery = Math.max(1, Math.ceil(length / Math.max(2, Math.floor(plotWidth / 78))));
    const labelIndices = [];
    for (let index = 0; index < length; index += labelEvery) labelIndices.push(index);
    if (length > 1 && length - 1 - labelIndices[labelIndices.length - 1] >= Math.ceil(labelEvery / 2)) labelIndices.push(length - 1);
    return {
      length, x, y, ticks, labelIndices, plotWidth,
      currentPath: line(current),
      areaPath: current.length > 1 ? `${line(current)} L${x(current.length - 1).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z` : "",
      previousPath: previous ? line(previous) : "",
    };
  }, [current, previous, metric, width]);

  if (!current.length) return null;

  const currentTotal = current.reduce((sum, point) => sum + metric.value(point), 0);
  const previousTotal = previous ? previous.reduce((sum, point) => sum + metric.value(point), 0) : null;
  const best = current.reduce((winner, point) => (metric.value(point) > metric.value(winner) ? point : winner), current[0]);
  const activeIndex = active ?? null;
  const activeCurrent = activeIndex != null ? current[activeIndex] : null;
  const activePrevious = activeIndex != null && previous ? previous[activeIndex] : null;
  const tooltipLeft = activeIndex != null ? Math.min(Math.max(chart.x(activeIndex), 110), width - 110) : 0;
  const unit = UNIT_LABEL[granularity];

  return <section className="sales-trend" aria-label="Sales over time">
    <div className="sales-trend-head">
      <div>
        <span className="sales-analytics-eyebrow">The trend</span>
        <h2>{metric.label} by {unit}</h2>
        <p>
          Average {metric.format(currentTotal / current.length)} per {unit}
          {metric.value(best) > 0 && <> · Best {unit}: <strong>{periodLabel(best, granularity)}</strong> ({metric.format(metric.value(best))})</>}
        </p>
      </div>
      <div className="sales-trend-controls">
        <div className="sales-trend-legend" aria-hidden="true">
          <span><i className="sales-trend-swatch sales-trend-swatch--current" />This period</span>
          {previous && <span><i className="sales-trend-swatch sales-trend-swatch--previous" />{comparisonLabel}</span>}
        </div>
        <div className="sales-trend-toggle" role="group" aria-label="Trend metric">
          {Object.entries(METRICS).map(([key, item]) => <button type="button" key={key} aria-pressed={metricKey === key} className={metricKey === key ? "is-selected" : ""} onClick={() => setMetricKey(key)}>{item.label}</button>)}
        </div>
      </div>
    </div>

    <div className="sales-trend-frame" ref={frameRef} onMouseLeave={() => setActive(null)}>
      <svg width={width} height={HEIGHT} role="img" aria-label={`${metric.label} by ${unit}: ${metric.format(currentTotal)} this period${previousTotal != null ? `, ${metric.format(previousTotal)} in the comparison period` : ""}`}>
        {chart.ticks.map((tick) => <g key={tick}>
          <line className={tick === 0 ? "sales-trend-zero" : "sales-trend-grid"} x1={PAD.left} x2={width - PAD.right} y1={chart.y(tick)} y2={chart.y(tick)} />
          <text className="sales-trend-axis" x={PAD.left - 10} y={chart.y(tick)} dy="0.32em" textAnchor="end">{metric.axis(tick)}</text>
        </g>)}
        {chart.labelIndices.filter((index) => current[index]).map((index) => <text key={current[index].start_date} className="sales-trend-axis" x={chart.x(index)} y={HEIGHT - 8}
          textAnchor={chart.length > 1 && index === 0 ? "start" : chart.length > 1 && index === chart.length - 1 ? "end" : "middle"}>{axisLabel(current[index], granularity)}</text>)}
        {chart.areaPath && <path className="sales-trend-area" d={chart.areaPath} />}
        {chart.previousPath && <path className="sales-trend-line sales-trend-line--previous" d={chart.previousPath} />}
        <path className="sales-trend-line sales-trend-line--current" d={chart.currentPath} />
        {current.length <= 62 && current.map((point, index) => <circle key={point.start_date} className="sales-trend-dot" cx={chart.x(index)} cy={chart.y(metric.value(point))} r={activeIndex === index ? 4.5 : 2.5} />)}
        {activeIndex != null && <line className="sales-trend-cursor" x1={chart.x(activeIndex)} x2={chart.x(activeIndex)} y1={PAD.top} y2={HEIGHT - PAD.bottom} />}
        {activePrevious && <circle className="sales-trend-dot sales-trend-dot--previous" cx={chart.x(activeIndex)} cy={chart.y(metric.value(activePrevious))} r={4} />}
        {Array.from({ length: chart.length }, (_, index) => {
          const half = chart.length > 1 ? chart.plotWidth / (chart.length - 1) / 2 : chart.plotWidth / 2;
          return <rect key={index} className="sales-trend-hit" x={chart.x(index) - half} y={PAD.top} width={half * 2} height={HEIGHT - PAD.top - PAD.bottom} onMouseEnter={() => setActive(index)} onClick={() => setActive(index)} />;
        })}
      </svg>
      {activeIndex != null && (activeCurrent || activePrevious) && <div className="sales-trend-tooltip" style={{ left: tooltipLeft }} role="status">
        {activeCurrent && <div><span>{periodLabel(activeCurrent, granularity)}</span><strong>{metric.format(metric.value(activeCurrent))}</strong></div>}
        {activePrevious && <div className="sales-trend-tooltip-previous"><span>{periodLabel(activePrevious, granularity)}</span><strong>{metric.format(metric.value(activePrevious))}</strong></div>}
        {activeCurrent && activePrevious && <TooltipChange current={metric.value(activeCurrent)} previous={metric.value(activePrevious)} format={metric.format} />}
      </div>}
    </div>

    <div className="sales-trend-data"><table aria-label={`${metric.label} by ${unit} data`}>
      <thead><tr><th scope="col">Period</th><th scope="col">This period</th>{previous && <th scope="col">Comparison period</th>}{previous && <th scope="col">Comparison</th>}</tr></thead>
      <tbody>{Array.from({ length: chart.length }, (_, index) => <tr key={index}>
        <th scope="row">{periodLabel(current[index], granularity) || "—"}</th>
        <td>{current[index] ? metric.format(metric.value(current[index])) : "—"}</td>
        {previous && <td>{periodLabel(previous[index], granularity) || "—"}</td>}
        {previous && <td>{previous[index] ? metric.format(metric.value(previous[index])) : "—"}</td>}
      </tr>)}</tbody>
    </table></div>
  </section>;
}

function TooltipChange({ current, previous, format }) {
  const difference = current - previous;
  const tone = difference > 0 ? "gain" : difference < 0 ? "decline" : "flat";
  return <div className={`sales-trend-tooltip-change sales-trend-tooltip-change--${tone}`}>
    {difference > 0 ? "+" : difference < 0 ? "−" : ""}{format(Math.abs(difference))}
    {previous !== 0 && <> ({difference > 0 ? "+" : difference < 0 ? "−" : ""}{Math.abs((difference / Math.abs(previous)) * 100).toFixed(1)}%)</>}
  </div>;
}
